import React, { useState, useEffect } from 'react';
import { X, Search, AlertTriangle, Star } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../../services/firebase';

const IngredientSearchModal = ({ isOpen, onClose, onSelect, selectedIngredients = [] }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [popularIngredients, setPopularIngredients] = useState([]);
  const [allergenIngredients, setAllergenIngredients] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPopularIngredients();
      fetchAllergenIngredients();
    }
  }, [isOpen]);

  useEffect(() => {
    const searchIngredients = async () => {
      setLoading(true);
      try {
        const searchLower = searchQuery.toLowerCase();
        const q = query(
          collection(db, 'ingredients'),
          where('nameLowercase', '>=', searchLower),
          where('nameLowercase', '<=', searchLower + ''),
          limit(20)
        );
        const snapshot = await getDocs(q);
        const ingredients = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSearchResults(ingredients);
      } catch (error) {
        console.error('Error searching ingredients:', error);
      } finally {
        setLoading(false);
      }
    };

    if (searchQuery.length >= 2) {
      searchIngredients();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchPopularIngredients = async () => {
    try {
      const q = query(
        collection(db, 'ingredients'),
        orderBy('usageCount', 'desc'),
        limit(10)
      );
      const snapshot = await getDocs(q);
      const ingredients = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPopularIngredients(ingredients);
    } catch (error) {
      console.error('Error fetching popular ingredients:', error);
    }
  };

  const fetchAllergenIngredients = async () => {
    try {
      const q = query(
        collection(db, 'ingredients'),
        where('isAllergen', '==', true),
        orderBy('usageCount', 'desc'),
        limit(20)
      );
      const snapshot = await getDocs(q);
      const ingredients = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllergenIngredients(ingredients);
    } catch (error) {
      console.error('Error fetching allergen ingredients:', error);
    }
  };

  const handleSelect = (ingredient) => {
    onSelect(ingredient.name);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-lg bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[80vh] bg-gradient-to-br from-[#F5F3EF] to-white rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-lg border-b border-[#2D5F4D]/10 flex items-center justify-between">
          <h2 className="text-title-2 text-[#2D5F4D] font-serif">Search Ingredients</h2>
          <button
            onClick={onClose}
            className="p-sm rounded-full hover:bg-[#2D5F4D]/10 transition-colors"
          >
            <X size={24} className="text-[#2D5F4D]" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-lg border-b border-[#2D5F4D]/10">
          <div className="relative">
            <Search size={20} className="absolute left-md top-1/2 -translate-y-1/2 text-[#2D5F4D]/40" />
            <input
              type="text"
              placeholder="Search ingredients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-lg py-md rounded-2xl bg-white border border-[#2D5F4D]/10 focus:border-[#2D5F4D]/30 text-body text-primary placeholder:text-text-tertiary/40 transition-all outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-lg">
          {searchQuery.length >= 2 ? (
            // Search Results
            <div>
              {loading ? (
                <p className="text-center text-text-secondary py-xl">Searching...</p>
              ) : searchResults.length > 0 ? (
                <div className="space-y-sm">
                  {searchResults.map((ingredient) => (
                    <button
                      key={ingredient.id}
                      onClick={() => handleSelect(ingredient)}
                      disabled={selectedIngredients.includes(ingredient.name)}
                      className={`w-full p-md rounded-2xl text-left transition-all ${
                        selectedIngredients.includes(ingredient.name)
                          ? 'bg-[#2D5F4D]/10 border-2 border-[#2D5F4D]/30 cursor-not-allowed'
                          : 'bg-white hover:bg-[#2D5F4D]/5 border border-[#2D5F4D]/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-callout font-semibold text-[#2D5F4D]">{ingredient.name}</p>
                          {ingredient.usageCount > 0 && (
                            <p className="text-caption text-[#8B7355]">{ingredient.usageCount} products</p>
                          )}
                        </div>
                        {ingredient.isAllergen && (
                          <AlertTriangle size={18} className="text-error" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-center text-text-secondary py-xl">No ingredients found</p>
              )}
            </div>
          ) : (
            // Popular & Allergen Sections
            <div className="space-y-xl">
              {/* Popular Ingredients */}
              <div>
                <div className="flex items-center gap-sm mb-md">
                  <Star size={20} className="text-[#2D5F4D]" />
                  <h3 className="text-title-3 text-[#2D5F4D]">Popular Ingredients</h3>
                </div>
                <p className="text-caption text-[#8B7355] mb-md">Commonly searched ingredients</p>
                <div className="flex flex-wrap gap-sm">
                  {popularIngredients.map((ingredient) => (
                    <button
                      key={ingredient.id}
                      onClick={() => handleSelect(ingredient)}
                      disabled={selectedIngredients.includes(ingredient.name)}
                      className={`px-md py-sm rounded-full text-caption font-semibold transition-all ${
                        selectedIngredients.includes(ingredient.name)
                          ? 'bg-[#2D5F4D]/20 text-[#2D5F4D] border-2 border-[#2D5F4D]/30 cursor-not-allowed'
                          : 'bg-white hover:bg-[#2D5F4D]/10 text-[#2D5F4D] border border-[#2D5F4D]/20'
                      }`}
                    >
                      {ingredient.name}
                      {ingredient.usageCount > 0 && (
                        <span className="ml-xs text-[#8B7355]">({ingredient.usageCount})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Common Allergens */}
              <div>
                <div className="flex items-center gap-sm mb-md">
                  <AlertTriangle size={20} className="text-error" />
                  <h3 className="text-title-3 text-[#2D5F4D]">Common Allergens</h3>
                </div>
                <p className="text-caption text-[#8B7355] mb-md">Ingredients that may cause allergic reactions</p>
                <div className="flex flex-wrap gap-sm">
                  {allergenIngredients.map((ingredient) => (
                    <button
                      key={ingredient.id}
                      onClick={() => handleSelect(ingredient)}
                      disabled={selectedIngredients.includes(ingredient.name)}
                      className={`px-md py-sm rounded-full text-caption font-semibold transition-all flex items-center gap-xs ${
                        selectedIngredients.includes(ingredient.name)
                          ? 'bg-error/20 text-error border-2 border-error/30 cursor-not-allowed'
                          : 'bg-error/10 hover:bg-error/20 text-error border border-error/20'
                      }`}
                    >
                      <AlertTriangle size={14} />
                      {ingredient.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IngredientSearchModal;
